import styled from 'styled-components';

import { formatToHoursAndMinutesFromSeconds } from '@/utils/time';
import { MetaLabel, BadgeLabel } from '@/ui/Label';
import { LONG_DATE_FORMAT, SHORT_DATE_FORMAT, useLocalizedDate } from '@/hooks/useLocalizedDate';
import { RLearningContentLocationData, RLearningType } from '@/store/v2';
import { formatSingleDateDependingOnTimezone, formatTimeDependingOnUserTimezone } from '@/utils/dates';
import { JOURNEY_INSIGHTS_LEARNING_TYPES_OPTIONS } from '@/features/journey/admin/atoms/JourneyInsightsTabsLearningItemsTypeFilter/JourneyInsightsTabsLearningItemsTypeFilter';
import { IconPropType } from '@/ui/Icon/types';
import { camelCaseToUpperUnderscoreCase } from '@/utils/text';
import { LearningCardVariant } from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCard';
import { useAdminContext } from '@/administration/store/AdminContext';

export type LearningCardMetaProps = {
  type?: string;
  level?: string;
  totalEffort?: number;
  totalLearningItems?: number;
  locations?: Omit<RLearningContentLocationData, '__typename'>[];
  availabilityScheduledAtTimezone?: number;
  timezone?: number;
  isOptional?: boolean;
  availabilityScheduledAt?: Date;
  variant?: LearningCardVariant;
  /** When true, meta labels are dimmed; badge row (Optional, Available on, etc.) stays full opacity. */
  disabled?: boolean;
};

/** Dims non-badge learning card content; keep badges outside this wrapper so they stay legible when the card is disabled. */
export const LearningCardDisabledContentDimmer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
`;

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaWrapStyled = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
`;

export const LearningCardMeta = ({
  totalEffort,
  level,
  locations,
  type,
  variant,
  isOptional,
  availabilityScheduledAt,
  availabilityScheduledAtTimezone,
  disabled,
}: LearningCardMetaProps): JSX.Element => {
  const { isAdmin } = useAdminContext();
  const effortText = totalEffort || totalEffort === 0 ? formatToHoursAndMinutesFromSeconds(totalEffort) : undefined;
  const availabilityScheduledAtTimestamp = availabilityScheduledAt ? new Date(availabilityScheduledAt).getTime() : NaN;
  const isAvailabilityInFuture =
    Number.isFinite(availabilityScheduledAtTimestamp) && availabilityScheduledAtTimestamp > Date.now();
  const shouldShowAvailabilityBadge = Boolean(availabilityScheduledAt) && (isAdmin || isAvailabilityInFuture);

  const isEvent = [RLearningType.LiveEvent, RLearningType.Seminar, RLearningType.Webinar].includes(type as RLearningType);

  const liveEventDate =
    isEvent && !!locations?.[0]?.startDate && !!locations?.[0]?.endDate
      ? formatTimeDependingOnUserTimezone({
          startTime: locations?.[0]?.startDate as Date,
          endTime: locations?.[0]?.endDate as Date,
          formatOptions: {
            showTimezone: true,
            startFormat: 'd MMM, HH:mm',
            endFormat: SHORT_DATE_FORMAT,
          },
        })
      : '';

  const availabilityScheduledAtText = shouldShowAvailabilityBadge
    ? `Available on ${formatSingleDateDependingOnTimezone({
        date: availabilityScheduledAt || new Date(),
        formatOptions: {
          showTimezone: true,
          format: 'd MMM, HH:mm',
        },
        timezoneOffsetInMinutes: availabilityScheduledAtTimezone,
      })}`
    : undefined;

  const formattedStart = useLocalizedDate(locations?.[0]?.startDate, LONG_DATE_FORMAT);
  const formattedEnd = useLocalizedDate(locations?.[0]?.endDate, SHORT_DATE_FORMAT);

  const locationDate = formattedStart && formattedEnd && `${formattedStart} - ${formattedEnd}`;

  const displayTypeMeta = JOURNEY_INSIGHTS_LEARNING_TYPES_OPTIONS.find(
    (item) => camelCaseToUpperUnderscoreCase(item.value) === type,
  );

  const displayTypeMetaIcon = displayTypeMeta?.prefixIcon;
  const displayTypeMetaText = displayTypeMeta?.displayValue;

  const shouldShowMetaBadges = isOptional || shouldShowAvailabilityBadge;

  return (
    <RootStyled>
      <LearningCardDisabledContentDimmer $disabled={disabled}>
        <MetaWrapStyled>
          {type && variant !== 'compact' && (
            <MetaLabel icon={displayTypeMetaIcon as IconPropType} text={displayTypeMetaText || type} />
          )}
          {level && !isEvent && <MetaLabel icon={['far', 'signal-strong']} text={level} />}
          {effortText && !isEvent && <MetaLabel icon={['far', 'clock']} text={effortText} />}
          {locationDate && (
            <MetaLabel icon={['far', 'calendar']} text={liveEventDate ? liveEventDate : `${formattedStart} - ${formattedEnd}`} />
          )}
        </MetaWrapStyled>
      </LearningCardDisabledContentDimmer>
      {shouldShowMetaBadges && (
        <MetaWrapStyled>
          {isOptional && <BadgeLabel badgeVariant="badge-recommended" text="Optional" noIcon />}
          {availabilityScheduledAtText && <BadgeLabel badgeVariant="badge-text" text={availabilityScheduledAtText} noIcon />}
        </MetaWrapStyled>
      )}
    </RootStyled>
  );
};
